// The local stores, Dexie over IndexedDB (D-81).
//
// Tokens and the profile live here and never in localStorage. Schema
// version 1 (INC-04a) held six stores: `session` (keyed records: tokens,
// profile, device, storage grant, last preparation, status), `catalogs`
// (clients, site types and sites, keyed by kind and id), `site_forms` (one
// effective form per site, by content hash), `visit_drafts`, `outbox_data`
// and `sync_log`.
//
// Schema version 2 (INC-05a, D-87): the draft is the whole visit document
// (values, observations, declared photos, the fixes, the close) with the
// device's revision; the outbox holds one kind of item, the document, at
// most one pending per visit; `site_last_values` keeps the last closed
// visit's values per site; the geolocation permission joins `session`.
// The upgrade keeps every draft and every item: a queued envelope becomes
// a queued document at revision 1.
//
// Schema version 3 (INC-05b, D-92): `photo_queue` holds the compressed bytes
// of every captured photo until they upload, one record per photo keyed by
// its identifier. The store is new, so the upgrade moves nothing.
//
// Schema version 4 (INC-06b-2b, D-187): `agenda` holds the operator's slots
// from today to six days later, with a freshness of its own in `session` so a
// failed day preparation never touches it; and every draft, every outbox item
// and every queued photo carries the operator that created it. The upgrade
// keeps all three queues and gives what it finds to the last known profile;
// without a profile the work stays on the device with no author, and nothing
// without an author is ever sent.
import Dexie, { type Table, type Transaction } from 'dexie';
import type {
  BootstrapRead,
  ClientRead,
  GeoPoint,
  LastValueRead,
  MeResponse,
  ObservationScope,
  OperatorRead,
  SiteRead,
  SiteTypeRead,
  VisitSyncItem,
} from '../api/types';
import type { FieldValue } from '../form/values';

export const DB_NAME = 'fieldcore';
export const SCHEMA_VERSION = 4;

// --- session -------------------------------------------------------------------------

export interface TokensRecord {
  key: 'tokens';
  accessToken: string;
  refreshToken: string;
  /** Seconds the access token lives, as the API said. */
  expiresIn: number;
  /** Epoch milliseconds when the access token expires. */
  expiresAt: number;
  issuedAt: number;
  /** INC-13 (CA-PLT-05): present for a support session, which has no refresh
   *  token and ends at this instant, as the API said it (ISO 8601). */
  supportEndsAt?: string;
}

export interface ProfileRecord {
  key: 'profile';
  me: MeResponse;
  loadedAt: number;
}

/** Persists across sessions: a new login names it so the server re-keys the row. */
export interface DeviceRecord {
  key: 'device';
  deviceId: string;
  label: string;
}

export interface StorageRecord {
  key: 'storage';
  supported: boolean;
  persisted: boolean;
  requestedAt: number;
}

export interface PreparationRecord {
  key: 'preparation';
  preparedAt: number;
  siteCount: number;
  formCount: number;
  downloadedForms: number;
  /** Last-values snapshots that travelled because their source visit changed. */
  downloadedLastValues?: number;
}

/** A terminal session state the application must show again after a reload. */
export interface StateRecord {
  key: 'state';
  status: 'active' | 'revoked' | 'suspended';
  since: number;
}

export interface BootstrapRecord {
  key: 'bootstrap';
  document: BootstrapRead;
  fetchedAt: number;
}

export type GeoPermissionState = 'granted' | 'denied' | 'prompt' | 'unsupported';

/** The location permission as last asked, at the day preparation (D-91). */
export interface GeoRecord {
  key: 'geo';
  state: GeoPermissionState;
  requestedAt: number;
}

/** When the agenda was last read from the server, and for whom (INC-06b-2b).
 *
 *  Its own record, outside `preparation`: the agenda is refreshed on its own
 *  policy and a day preparation that fails never touches it (criterion 53).
 *  `planningEnabled` false is the organization with planning off: there is no
 *  agenda and its absence is not staleness. */
export interface AgendaFreshnessRecord {
  key: 'agenda';
  fetchedAt: number;
  /** The operator the stored slots belong to; another operator's agenda is never shown. */
  operatorId: string;
  /** First and last day covered, as dates in the organization's timezone. */
  from: string;
  to: string;
  planningEnabled: boolean;
}

/** The invitation to install, dismissed on this device (D-208).
 *
 *  It belongs to the device and not to the session, like the device's own
 *  identifier: the operator said no on this tablet, and the sign out of one
 *  person does not make the invitation come back for the next. */
export interface InstallRecord {
  key: 'install';
  dismissed: boolean;
  at: number;
}

export type SessionRecord =
  | TokensRecord
  | ProfileRecord
  | DeviceRecord
  | StorageRecord
  | PreparationRecord
  | StateRecord
  | BootstrapRecord
  | GeoRecord
  | AgendaFreshnessRecord
  | InstallRecord;

export type SessionKey = SessionRecord['key'];

// --- catalogs ------------------------------------------------------------------------

export type CatalogRecord =
  | { kind: 'client'; id: string; data: ClientRead }
  | { kind: 'site_type'; id: string; data: SiteTypeRead }
  | { kind: 'site'; id: string; data: SiteRead }
  // INC-06b-2b (D-152): the organization's active operators, so a crew shows
  // names without connectivity. Identifier and name, nothing else.
  | { kind: 'operator'; id: string; data: OperatorRead };

export type CatalogKind = CatalogRecord['kind'];

// --- site forms ----------------------------------------------------------------------

export interface SiteFormRecord {
  site_id: string;
  content_hash: string;
  template_version_id: string;
  schema_version: number;
  computed_at: string;
  /** The effective form document as the API served it; parsed on render. */
  document: unknown;
  stored_at: number;
}

// --- last values per site (INC-05a) -------------------------------------------------

export interface SiteLastValuesRecord {
  site_id: string;
  visit_id: string | null;
  closed_at: string | null;
  /** The entity tag the API answered with: sent back so the snapshot travels only when changed. */
  etag: string;
  values: LastValueRead[];
  fetched_at: number;
}

// --- agenda (INC-06b-2b, D-151, D-152) ------------------------------------------------

/** One slot of the operator's agenda, as GET /api/v1/agenda answered it.
 *
 *  Kept whole so the seven days render without connectivity: the crew by
 *  identifier, both reasons, and the original date a rescheduled slot is
 *  marked by (D-188). The quota and the compliance of the period are not
 *  here and never travel to the device (D-180). */
export interface AgendaRecord {
  /** The slot's identifier; a planned visit names it. */
  id: string;
  site_id: string;
  period_start: string;
  period_end: string;
  suggested_date: string;
  /** Set when the slot was moved: what marks it rescheduled, not its status (D-188). */
  original_date: string | null;
  source: string;
  status: string;
  /** The caller's place in the crew: `lead` or `companion`. */
  role: string;
  /** The slot's lead when the caller is a companion; null when the caller is the lead. */
  lead_operator_id: string | null;
  /** The whole crew of the slot, the initial value of a visit's crew (D-152). */
  crew_lead_operator_id: string | null;
  companion_operator_ids: string[];
  reschedule_reason: string | null;
  cancel_reason: string | null;
  /** The day the visit that consumed the slot was done, as the server records
   *  it (D-225), and null while nothing consumed it. It is how a slot another
   *  device closed carries its day; the store is replaced whole on every
   *  refresh, so a record written before this field existed is read as null
   *  and is gone with the next refresh. */
  consumed_date: string | null;
  /** B-40, tramo 2a (R-29): the visit's hours, "HH:MM" local to the site, both
   *  or neither. A record written before they travelled has neither, and reads
   *  as a visit with no hours; no version of the store is needed for them,
   *  since no index names them. */
  start_time?: string | null;
  end_time?: string | null;
  /** B-76 (D-489): the hours it had before it was moved, "HH:MM", both or
   *  neither. A record written before they travelled has neither, and reads as
   *  a visit with no hours before it was moved; no index names them either. */
  original_start_time?: string | null;
  original_end_time?: string | null;
  /** The operator whose agenda this is: another operator's slots never show. */
  operator_id: string;
  fetched_at: number;
}

// --- visit drafts --------------------------------------------------------------------

/** `preparing` before the visit starts, `started` while it is captured,
 *  `closing` once the close is queued and until the server answers,
 *  `frozen` when the server closed it or refused every later revision. */
export type DraftStatus = 'preparing' | 'started' | 'closing' | 'frozen';
export type DraftSyncStatus = 'pending_local' | 'data_synced_photos_pending' | 'complete';
export type FrozenReason = 'closed' | 'visit_closed';

export interface DraftObservation {
  client_generated_id: string;
  scope: ObservationScope;
  field_definition_id: string | null;
  template_group_id: string | null;
  text: string;
  recorded_at: string;
  geo: GeoPoint | null;
}

/** A photo declared by the document; the bytes and the capture arrive with INC-05b. */
export interface DraftPhoto {
  client_generated_id: string;
  template_group_id: string;
  field_definition_id: string | null;
  observation_client_generated_id: string | null;
  captured_at: string;
  checksum: string;
  bytes: number;
  geo: GeoPoint | null;
}

export type MissingKind =
  | 'required_field'
  | 'min_photos'
  | 'observation_red'
  | 'observation_fail'
  | 'photo_fail'
  // INC-14a-3f (point 6): a numeric value out of limit without a photo of the
  // field when the field asks for one on failure or out of limit.
  | 'photo_red'
  | 'observation_geofence'
  | 'geo_required';

export interface MissingItem {
  kind: MissingKind;
  template_group_id: string | null;
  field_definition_id: string | null;
}

/** Why an unplanned visit was started (D-182). The code travels and is
 *  persisted; the text a person reads is the catalog's. `other` carries the
 *  operator's own words beside it. */
export type UnplannedReasonCode = 'claim' | 'incident' | 'follow_up' | 'extra' | 'other';

export const UNPLANNED_REASON_CODES: readonly UnplannedReasonCode[] = [
  'claim',
  'incident',
  'follow_up',
  'extra',
  'other',
];

/** Why a planned visit became unplanned on the device (D-160): the slot is
 *  gone from the server, cancelled, already consumed, or planning was turned
 *  off. Always shown to the operator, never silent. */
export type ConversionReason =
  'slot_not_found' | 'slot_cancelled' | 'slot_consumed' | 'planning_disabled';

export interface VisitDraftRecord {
  client_generated_id: string;
  site_id: string;
  status: DraftStatus;
  started_at: string | null;
  unplanned_reason: string | null;
  template_version_id: string;
  form_content_hash: string;
  /** The form the visit renders against, frozen when the visit starts (D-49):
   *  a later change of the site's form never rewrites a started visit. */
  document: unknown;
  values: Record<string, FieldValue>;
  /** INC-14a-1 (B-14): when the person last wrote each value, ISO 8601, by
   *  field: the instant the value travels with, and its alert with it. Editing
   *  another value of the visit leaves it as it is. A draft saved before the
   *  field existed reads without it; its values take the draft's last edit
   *  until the next value written fixes it for them (drafts.ts). */
  value_times?: Record<string, string>;
  observations: DraftObservation[];
  photos: DraftPhoto[];
  geo_start: GeoPoint | null;
  geo_end: GeoPoint | null;
  ended_at: string | null;
  /** The device's monotonic revision of the document (D-87); 0 before the start. */
  client_revision: number;
  /** The last revision the server acknowledged. */
  synced_revision: number;
  /** What the server said was missing on the last refused close, or null. */
  missing: MissingItem[] | null;
  frozen_reason: FrozenReason | null;
  server_visit_id: string | null;
  /** The server's status, once known. */
  server_status: string | null;
  version: number | null;
  sync_status: DraftSyncStatus;
  /** INC-06b-2b (D-113): planned when the visit fulfils a slot, unplanned otherwise. */
  visit_kind: 'planned' | 'unplanned';
  /** The slot a planned visit names; null on an unplanned one. */
  visit_slot_id: string | null;
  /** The reason's code (D-182); null while the visit has not started. */
  unplanned_reason_code: UnplannedReasonCode | null;
  /** The crew's companions by operator identifier, the slot's as the initial value (D-152). */
  companions: string[];
  /** Companions the server refused, by identifier and code: marked, and removable (D-110). */
  rejected_companions: { operator_id: string; code: string }[];
  /** Why the visit stopped being planned, once it did; shown to the operator (D-160). */
  converted_reason: ConversionReason | null;
  /** The last rejection no revision can settle, by code: shown and never discarded (D-176). */
  rejected_code: string | null;
  /** INC-06b-2b (D-187): the operator that created the draft. Only its own session sends it. */
  author_id: string | null;
  created_at: number;
  updated_at: number;
}

// --- outbox and log -------------------------------------------------------------------

export type OutboxStatus = 'queued' | 'sending' | 'synced' | 'rejected';

export interface OutboxRecord {
  id: string;
  kind: 'visit_document';
  client_generated_id: string;
  payload: VisitSyncItem;
  /** The revision the payload carries. */
  client_revision: number;
  /** 1 when the draft changed while this item was in flight: re-enqueued after the cycle. */
  dirty: 0 | 1;
  status: OutboxStatus;
  enqueued_at: number;
  attempts: number;
  next_attempt_at: number | null;
  last_code: string | null;
  last_http_status: number | null;
  /** INC-06b-2b (D-187): the operator that created the item. Only its own session sends it. */
  author_id: string | null;
  updated_at: number;
}

export type SyncOutcome =
  | 'success'
  | 'unchanged'
  | 'rejected'
  | 'network_error'
  | 'server_error'
  | 'rate_limited'
  | 'unauthorized'
  | 'forbidden';

export interface SyncLogRecord {
  id?: number;
  outbox_id: string;
  attempt: number;
  at: number;
  outcome: SyncOutcome;
  http_status: number | null;
  code: string | null;
  next_delay_ms: number | null;
}

// --- photo queue (INC-05b) ------------------------------------------------------------

/** `pending` until sent, `uploading` while its PUT is in flight, `uploaded`
 *  once the server holds the bytes, `rejected` on a terminal answer. */
export type PhotoQueueStatus = 'pending' | 'uploading' | 'uploaded' | 'rejected';

export interface PhotoQueueRecord {
  /** The photo's identifier, the one its declaration carries. */
  client_generated_id: string;
  visit_client_generated_id: string;
  /** The compressed JPEG while it waits; null once uploaded, the bytes discarded. */
  data: ArrayBuffer | null;
  checksum: string;
  bytes: number;
  /** The draft revision that first declares the photo: it uploads once the server applied it. */
  declared_revision: number;
  status: PhotoQueueStatus;
  attempts: number;
  next_attempt_at: number | null;
  last_code: string | null;
  last_http_status: number | null;
  /** The server's identifier, from the upload's answer; it names the thumbnail. */
  server_photo_id: string | null;
  /** 1 when the operator deleted the photo while its upload was in flight. */
  deleted: 0 | 1;
  /** INC-06b-2b (D-187, D-144): the operator that captured the photo. Only it uploads the bytes. */
  author_id: string | null;
  created_at: number;
  updated_at: number;
}

// --- the database ---------------------------------------------------------------------

export type FieldcoreDb = Dexie & {
  session: Table<SessionRecord, SessionKey>;
  catalogs: Table<CatalogRecord, [CatalogKind, string]>;
  site_forms: Table<SiteFormRecord, string>;
  site_last_values: Table<SiteLastValuesRecord, string>;
  visit_drafts: Table<VisitDraftRecord, string>;
  outbox_data: Table<OutboxRecord, string>;
  sync_log: Table<SyncLogRecord, number>;
  photo_queue: Table<PhotoQueueRecord, string>;
  agenda: Table<AgendaRecord, string>;
};

const VERSION_1_STORES = {
  session: 'key',
  catalogs: '[kind+id], kind',
  site_forms: 'site_id, content_hash',
  visit_drafts: 'client_generated_id, site_id, status, updated_at',
  outbox_data: 'id, status, enqueued_at, client_generated_id',
  sync_log: '++id, outbox_id, at',
};

const VERSION_2_STORES = {
  ...VERSION_1_STORES,
  site_last_values: 'site_id',
  outbox_data: 'id, status, enqueued_at, client_generated_id, dirty',
};

const VERSION_3_STORES = {
  ...VERSION_2_STORES,
  photo_queue: 'client_generated_id, status, visit_client_generated_id, created_at',
};

// INC-06b-2b: the agenda joins the stores, and the three queues gain the
// author they are filtered by, so a cycle reads only the session's own work.
const VERSION_4_STORES = {
  ...VERSION_3_STORES,
  agenda: 'id, suggested_date, operator_id, site_id',
  visit_drafts: 'client_generated_id, site_id, status, updated_at, author_id, visit_slot_id',
  outbox_data: 'id, status, enqueued_at, client_generated_id, dirty, author_id',
  photo_queue: 'client_generated_id, status, visit_client_generated_id, created_at, author_id',
};

/** The shape of a draft before version 2, as far as the upgrade reads it. */
interface DraftV1 {
  client_generated_id: string;
  status: string;
  values?: Record<string, FieldValue>;
  version?: number | null;
}

interface OutboxV1 {
  id: string;
  kind: string;
  client_generated_id: string;
  payload: Record<string, unknown>;
  status: OutboxStatus;
  enqueued_at: number;
  updated_at: number;
}

/** Version 1 to 2 (INC-05a): every draft gains the document members at
 *  revision 0; every started draft moves to revision 1 and gets exactly one
 *  queued document carrying its values, rewriting its pending envelope item
 *  when there is one. Nothing is dropped. */
async function upgradeToVersion2(transaction: Transaction): Promise<void> {
  const now = Date.now();
  const drafts = transaction.table('visit_drafts');
  const outbox = transaction.table('outbox_data');
  const items = (await outbox.toArray()) as OutboxV1[];
  const pendingByVisit = new Map<string, OutboxV1>();
  for (const item of items) {
    if (item.status === 'queued' || item.status === 'sending') {
      pendingByVisit.set(item.client_generated_id, item);
    }
  }
  await drafts.toCollection().modify((draft: DraftV1 & Partial<VisitDraftRecord>) => {
    const started = draft.status === 'started';
    draft.observations ??= [];
    draft.photos ??= [];
    draft.geo_start ??= null;
    draft.geo_end ??= null;
    draft.ended_at ??= null;
    draft.client_revision = started ? 1 : 0;
    draft.synced_revision = 0;
    draft.missing ??= null;
    draft.frozen_reason ??= null;
    draft.server_status ??= null;
    draft.values ??= {};
  });
  const upgraded = (await drafts.toArray()) as VisitDraftRecord[];
  for (const item of items) {
    const draft = upgraded.find(
      (candidate) => candidate.client_generated_id === item.client_generated_id,
    );
    const document =
      draft === undefined ? envelopeAsDocument(item.payload) : documentOfDraftV2(draft);
    const pending = item.status === 'queued' || item.status === 'sending';
    await outbox.put({
      ...item,
      kind: 'visit_document',
      payload: document,
      client_revision: pending ? 1 : 0,
      dirty: 0,
      status: pending ? 'queued' : item.status,
      updated_at: now,
    });
    // The author is written by the version-4 upgrade, which runs after this one.
  }
  for (const draft of upgraded) {
    if (draft.status !== 'started' || pendingByVisit.has(draft.client_generated_id)) continue;
    // A started visit whose envelope was already delivered: its values
    // travel as revision 1, so nothing typed before the upgrade is lost.
    await outbox.add({
      id: crypto.randomUUID(),
      kind: 'visit_document',
      client_generated_id: draft.client_generated_id,
      payload: documentOfDraftV2(draft),
      client_revision: 1,
      dirty: 0,
      status: 'queued',
      enqueued_at: now,
      attempts: 0,
      next_attempt_at: null,
      last_code: null,
      last_http_status: null,
      // The version-4 upgrade, which runs after this one, names the author.
      author_id: null,
      updated_at: now,
    } satisfies OutboxRecord);
  }
}

function envelopeAsDocument(payload: Record<string, unknown>): VisitSyncItem {
  return {
    ...(payload as unknown as VisitSyncItem),
    client_revision: 1,
    version: 0,
    values: [],
    observations: [],
    photos: [],
  };
}

// Set by the document module at load time: the upgrade builds documents
// with the same code the outbox uses, without a circular import.
let documentBuilder: ((draft: VisitDraftRecord) => VisitSyncItem) | null = null;

export function registerDocumentBuilder(builder: (draft: VisitDraftRecord) => VisitSyncItem): void {
  documentBuilder = builder;
}

function documentOfDraftV2(draft: VisitDraftRecord): VisitSyncItem {
  if (documentBuilder === null) throw new Error('document builder is not registered');
  return documentBuilder(draft);
}

/** Version 3 to 4 (INC-06b-2b, D-187): every draft, every outbox item and
 *  every queued photo is given the last known profile's user as its author,
 *  and gains the planning members an envelope born before planning had none.
 *  Nothing is dropped: with no profile on the device the work stays, without
 *  an author, and no session sends it until its author signs in and claims it. */
async function upgradeToVersion4(transaction: Transaction): Promise<void> {
  const session = transaction.table('session');
  const profile = (await session.get('profile')) as ProfileRecord | undefined;
  const author = profile?.me.user.id ?? null;
  await transaction
    .table('visit_drafts')
    .toCollection()
    .modify((draft: Partial<VisitDraftRecord>) => {
      draft.author_id = author;
      // Every visit the device holds was born unplanned: planning reached
      // the device with this very version.
      draft.visit_kind ??= 'unplanned';
      draft.visit_slot_id ??= null;
      draft.unplanned_reason_code ??= null;
      draft.companions ??= [];
      draft.rejected_companions ??= [];
      draft.converted_reason ??= null;
      draft.rejected_code ??= null;
    });
  await transaction
    .table('outbox_data')
    .toCollection()
    .modify((item: Partial<OutboxRecord>) => {
      item.author_id = author;
    });
  // The photo queue is rewritten record by record rather than modified in
  // place: a `modify` drops the `data` member, because Dexie applies a
  // property diff and an ArrayBuffer does not survive it — the bytes of every
  // photo waiting to upload would be lost. Reading and putting the whole
  // record keeps them.
  const photos = (await transaction.table('photo_queue').toArray()) as PhotoQueueRecord[];
  await transaction
    .table('photo_queue')
    .bulkPut(photos.map((photo) => ({ ...photo, author_id: author })));
}

export function openDatabase(name: string = DB_NAME): FieldcoreDb {
  const database = new Dexie(name) as FieldcoreDb;
  database.version(1).stores(VERSION_1_STORES);
  database.version(2).stores(VERSION_2_STORES).upgrade(upgradeToVersion2);
  database.version(3).stores(VERSION_3_STORES);
  database.version(SCHEMA_VERSION).stores(VERSION_4_STORES).upgrade(upgradeToVersion4);
  return database;
}

/** The version-1 schema alone, for the upgrade test: what a device
 *  running the previous release holds. */
export function openDatabaseVersion1(name: string): Dexie {
  const database = new Dexie(name);
  database.version(1).stores(VERSION_1_STORES);
  return database;
}

/** The version-3 schema alone, for the upgrade test of INC-06b-2b: what a
 *  device running 0.6.0-rc.6 holds, drafts and both queues included. */
export function openDatabaseVersion3(name: string): Dexie {
  const database = new Dexie(name);
  database.version(1).stores(VERSION_1_STORES);
  database.version(2).stores(VERSION_2_STORES);
  database.version(3).stores(VERSION_3_STORES);
  return database;
}

export const db: FieldcoreDb = openDatabase();

/** Read one keyed session record with its type narrowed by key. */
export async function getSessionRecord<K extends SessionKey>(
  database: FieldcoreDb,
  key: K,
): Promise<Extract<SessionRecord, { key: K }> | undefined> {
  const record = await database.session.get(key);
  return record as Extract<SessionRecord, { key: K }> | undefined;
}

/** Outbox items that still need the operator's or the server's attention. */
export const PENDING_STATUSES: readonly OutboxStatus[] = ['queued', 'sending', 'rejected'];

/** What still waits for connectivity, and only that (INC-06b-2b, D-176): a
 *  terminal rejection is not waiting for anything, so it blocks no sign out. */
export const WAITING_STATUSES: readonly OutboxStatus[] = ['queued', 'sending'];

/** Photos not yet on the server, the terminal ones included (INC-05b). */
export const PENDING_PHOTO_STATUSES: readonly PhotoQueueStatus[] = [
  'pending',
  'uploading',
  'rejected',
];

/** Photo bytes that still wait for connectivity; a rejected photo waits for nothing. */
export const WAITING_PHOTO_STATUSES: readonly PhotoQueueStatus[] = ['pending', 'uploading'];

export async function pendingPhotoCount(database: FieldcoreDb): Promise<number> {
  return database.photo_queue
    .where('status')
    .anyOf([...PENDING_PHOTO_STATUSES])
    .count();
}

/** INC-05b: both queues. Signing out stays blocked while a photo waits too. */
export async function pendingCount(database: FieldcoreDb): Promise<number> {
  const data = await database.outbox_data
    .where('status')
    .anyOf([...PENDING_STATUSES])
    .count();
  return data + (await pendingPhotoCount(database));
}

/** What the signed-in operator has on the device, of both queues (D-187).
 *
 *  Only its own: another operator's work is counted by `foreignPendingCount`,
 *  shown without detail and left exactly as it is until that person signs in. */
export async function ownPendingCount(
  database: FieldcoreDb,
  authorId: string | null,
): Promise<number> {
  if (authorId === null) return 0;
  const data = await database.outbox_data
    .where('author_id')
    .equals(authorId)
    .filter((item) => PENDING_STATUSES.includes(item.status))
    .count();
  const photos = await database.photo_queue
    .where('author_id')
    .equals(authorId)
    .filter((photo) => PENDING_PHOTO_STATUSES.includes(photo.status))
    .count();
  return data + photos;
}

/** What blocks the operator from signing out (D-176): its own work that only
 *  waits for signal. A terminal rejection is shown and never discarded, and
 *  the operator can sign out with one. */
export async function ownWaitingCount(
  database: FieldcoreDb,
  authorId: string | null,
): Promise<number> {
  if (authorId === null) return 0;
  const data = await database.outbox_data
    .where('author_id')
    .equals(authorId)
    .filter((item) => WAITING_STATUSES.includes(item.status))
    .count();
  const photos = await database.photo_queue
    .where('author_id')
    .equals(authorId)
    .filter((photo) => WAITING_PHOTO_STATUSES.includes(photo.status))
    .count();
  return data + photos;
}

/** Another operator's pending work on this device: how many items, and whose.
 *
 *  Nothing of its content is read: the operator sees that work of another
 *  person waits and that it travels when that person signs in, and no more. */
export async function foreignPendingCount(
  database: FieldcoreDb,
  authorId: string | null,
): Promise<{ items: number; operators: number }> {
  const data = await database.outbox_data
    .where('status')
    .anyOf([...PENDING_STATUSES])
    .filter((item) => item.author_id !== authorId)
    .toArray();
  const photos = await database.photo_queue
    .where('status')
    .anyOf([...PENDING_PHOTO_STATUSES])
    .filter((photo) => photo.author_id !== authorId)
    .toArray();
  const authors = new Set<string>();
  for (const item of data) if (item.author_id !== null) authors.add(item.author_id);
  for (const photo of photos) if (photo.author_id !== null) authors.add(photo.author_id);
  return { items: data.length + photos.length, operators: authors.size };
}
